import { trackRedditLead } from "~/web/site/reddit-pixel";
import { formParts, turnstileSubmit } from "~/web/site/turnstile";
import { track } from "~/web/src/analytics";
import { keepFirstTouch } from "~/web/src/first-touch";
import { trackLead, trackSignUp } from "~/web/src/google-analytics";

// The login page (docs/project/own-auth-spec.md, decision 12). Two ways in:
// with ?token= (a mailed link) the field hides and the page spends the token
// with one POST — a scanner's GET spends nothing — then goes where the server
// says; without one, the address field + Turnstile → POST /api/auth/link.
// A known address is mailed a link ("check your mail"); a new one is an
// account, signed in at once and sent to /projects unverified. A spent or
// expired token shows its sentence and the field, to ask for a fresh link.
// `next` is passed through as the URL carries it; the server decides what it
// may be (a same-origin path, or the admin host's URL — auth/routes.ts
// safeNext) and the mail and the consume's answer carry its verdict. A
// signed-in visitor without a token never reaches this page: the server
// sends them to `next` (auth/routes.ts).

const CHECK_MAIL = "Check your mail — the link works for 30 days.";

export function loginPage(main: HTMLElement): void {
  const form = main.querySelector<HTMLFormElement>("form[data-login-form]");
  const states = [...main.querySelectorAll<HTMLElement>("[data-state]")];
  const parts = form && formParts(form);
  if (!form || !parts) return;
  const show = (state: string) => {
    for (const el of states) el.hidden = el.dataset.state !== state;
  };
  const params = new URLSearchParams(location.search);
  const next = params.get("next") ?? undefined;
  const field = (name: string) => String(new FormData(form).get(name) ?? "").trim();

  const { fail } = turnstileSubmit(parts, {
    send: async (turnstile) => {
      await keepFirstTouch();
      let res: Response;
      let json: { sent?: boolean; next?: string; error?: string };
      try {
        res = await fetch("/api/auth/link", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: field("email"), next, turnstile }),
        });
        json = (await res.json().catch(() => ({}))) as typeof json;
      } catch {
        fail("Couldn't reach Featurette — check your connection and try again.");
        return;
      }
      if (!res.ok) {
        fail(json.error ?? `Something went wrong (${res.status}). Try again in a moment.`);
        return;
      }
      if (typeof json.next === "string") {
        // The account was made: the funnel's lead (own-auth-spec, 2026-10-30), as the landing
        // submit is — the Reddit Lead and GA's generate_lead. pixel.js posts with keepalive, so
        // the navigation doesn't cancel it; GA's event rides to the next page.
        track("signup.completed", {}, { instant: true });
        trackRedditLead();
        trackLead();
        location.assign(json.next);
        return;
      }
      track("signin.linked", {}, { instant: true });
      const sent = states.find((s) => s.dataset.state === "sent");
      if (sent) sent.textContent = CHECK_MAIL;
      show("sent");
    },
  });

  const token = params.get("token");
  if (!token) {
    show("form");
    return;
  }
  show("consuming");
  void (async () => {
    let res: Response;
    let json: { next?: string; verified?: boolean; error?: string };
    try {
      res = await fetch("/api/auth/consume", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      json = (await res.json().catch(() => ({}))) as typeof json;
    } catch {
      show("form");
      fail("Couldn't reach Featurette — check your connection and try again.");
      return;
    }
    if (res.ok && typeof json.next === "string") {
      // `verified`: this click registered the account (own-auth-spec, "Lead and registration") —
      // GA's sign_up, the conversion Google Ads imports, sent by the page at `next`. Reddit's
      // SIGN_UP is the server's, off the lead's stored signals, since this click is often on
      // another device.
      if (json.verified) trackSignUp("email");
      location.assign(json.next);
      return;
    }
    show("form");
    fail(json.error ?? `Something went wrong (${res.status}). Try again in a moment.`);
  })();
}
